import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Bell } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { getApiBase } from '../../utils/apiBase';
import {
  REMINDER_TYPE_LABELS, REMINDER_TIMING_LABELS, REMINDER_REPEAT_LABELS, formatReminderDate,
} from '../../utils/reminders';

const API = getApiBase();

const MONTHS = [
  { v: 1, l: 'January' }, { v: 2, l: 'February' }, { v: 3, l: 'March' },
  { v: 4, l: 'April' }, { v: 5, l: 'May' }, { v: 6, l: 'June' },
  { v: 7, l: 'July' }, { v: 8, l: 'August' }, { v: 9, l: 'September' },
  { v: 10, l: 'October' }, { v: 11, l: 'November' }, { v: 12, l: 'December' },
];

const DEFAULT_FORM = {
  title: '',
  reminder_type: 'custom',
  date_month: 1,
  date_day: 1,
  date_year: '',
  repeat_rule: 'yearly',
  reminder_timing: 'same_day',
  channels: ['in_app'],
  custom_message: '',
  enabled: true,
};

/**
 * @param {{
 *   open: boolean,
 *   onOpenChange: (open: boolean) => void,
 *   onSaved: (reminder: object) => void,
 *   prefill?: Partial<typeof DEFAULT_FORM> & { person_id?: string, person_name?: string, tree_id?: string },
 *   editReminder?: object | null,
 * }} props
 */
export function AddReminderDialog({ open, onOpenChange, onSaved, prefill = {}, editReminder = null }) {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return;
    if (editReminder) {
      setForm({
        ...DEFAULT_FORM,
        ...editReminder,
        date_year: editReminder.date_year || '',
        custom_message: editReminder.custom_message || '',
      });
    } else {
      setForm({ ...DEFAULT_FORM, ...prefill, date_year: prefill.date_year || '' });
    }
  // JSON.stringify(prefill) is intentional to deep-compare prefill on dialog open
  }, [open, editReminder]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const toggleChannel = (ch) => {
    setForm((f) => ({
      ...f,
      channels: f.channels.includes(ch)
        ? f.channels.filter((c) => c !== ch)
        : [...f.channels, ch],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Please enter a title.'); return; }
    if (!form.date_month || !form.date_day) { toast.error('Please select a month and day.'); return; }

    setSaving(true);
    try {
      const payload = {
        ...form,
        date_month: Number(form.date_month),
        date_day: Number(form.date_day),
        date_year: form.date_year ? Number(form.date_year) : null,
        tree_id: prefill.tree_id || editReminder?.tree_id || null,
        person_id: prefill.person_id || editReminder?.person_id || null,
        person_name: prefill.person_name || editReminder?.person_name || null,
        person_image_url: prefill.person_image_url || editReminder?.person_image_url || null,
        custom_message: form.custom_message.trim() || null,
      };

      let result;
      if (editReminder) {
        const resp = await axios.patch(`${API}/reminders-api/${editReminder.id}`, payload);
        result = resp.data;
        toast.success('Reminder updated.');
      } else {
        const resp = await axios.post(`${API}/reminders-api`, payload);
        result = resp.data;
        toast.success('Reminder saved.', { description: `Next: ${formatReminderDate(result.date_month, result.date_day, result.date_year)}` });
      }
      onSaved?.(result);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Could not save reminder.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md border-[#e8e2d8] bg-[#fffcf7] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-primary/70" />
            <DialogTitle className="font-serif text-xl font-normal text-[#1c1917]">
              {editReminder ? 'Edit reminder' : 'Add a reminder'}
            </DialogTitle>
          </div>
          <DialogDescription className="text-sm text-[#5c534a]">
            {prefill.person_name
              ? `Setting a reminder for ${prefill.person_name}.`
              : 'Create a family date reminder or tribute notice.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          {/* Type */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium uppercase tracking-[0.1em] text-muted-foreground">
              Reminder type
            </Label>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {Object.entries(REMINDER_TYPE_LABELS).map(([val, label]) => (
                <button
                  key={val}
                  type="button"
                  onClick={() => { set('reminder_type', val); if (!form.title || Object.keys(REMINDER_TYPE_LABELS).includes(form.title)) set('title', label + (prefill.person_name ? ` — ${prefill.person_name}` : '')); }}
                  className={`rounded-lg border px-3 py-2 text-xs font-medium transition ${
                    form.reminder_type === val
                      ? 'border-primary/40 bg-primary/8 text-primary'
                      : 'border-border/60 text-muted-foreground hover:border-border'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div className="space-y-1.5">
            <Label htmlFor="rem-title" className="text-sm font-medium">Title</Label>
            <Input id="rem-title" value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="e.g., Louise's Birthday" className="border-[#e8e2d8]" required />
          </div>

          {/* Date */}
          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1.5 col-span-1">
              <Label className="text-xs font-medium uppercase tracking-[0.1em] text-muted-foreground">Month</Label>
              <select
                value={form.date_month}
                onChange={(e) => set('date_month', Number(e.target.value))}
                className="w-full rounded-lg border border-[#e8e2d8] bg-white px-2 py-2 text-sm"
              >
                {MONTHS.map((m) => <option key={m.v} value={m.v}>{m.l.slice(0, 3)}</option>)}
              </select>
            </div>
            <div className="space-y-1.5 col-span-1">
              <Label className="text-xs font-medium uppercase tracking-[0.1em] text-muted-foreground">Day</Label>
              <Input
                type="number" min={1} max={31} value={form.date_day}
                onChange={(e) => set('date_day', Number(e.target.value))}
                className="border-[#e8e2d8]"
              />
            </div>
            <div className="space-y-1.5 col-span-1">
              <Label className="text-xs font-medium uppercase tracking-[0.1em] text-muted-foreground">Year (opt)</Label>
              <Input
                type="number" min={1800} max={2100} value={form.date_year || ''}
                onChange={(e) => set('date_year', e.target.value)}
                placeholder="—"
                className="border-[#e8e2d8]"
              />
            </div>
          </div>

          {/* Repeat + Timing */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium uppercase tracking-[0.1em] text-muted-foreground">Repeats</Label>
              <select
                value={form.repeat_rule}
                onChange={(e) => set('repeat_rule', e.target.value)}
                className="w-full rounded-lg border border-[#e8e2d8] bg-white px-2 py-2 text-sm"
              >
                {Object.entries(REMINDER_REPEAT_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium uppercase tracking-[0.1em] text-muted-foreground">Notify me</Label>
              <select
                value={form.reminder_timing}
                onChange={(e) => set('reminder_timing', e.target.value)}
                className="w-full rounded-lg border border-[#e8e2d8] bg-white px-2 py-2 text-sm"
              >
                {Object.entries(REMINDER_TIMING_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>

          {/* Channels */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium uppercase tracking-[0.1em] text-muted-foreground">Channels</Label>
            <div className="flex flex-wrap gap-2">
              {[
                { val: 'in_app', label: 'In-app', active: true },
                { val: 'email', label: 'Email', active: true },
                { val: 'browser_push', label: 'Browser push', active: false, soon: true },
              ].map(({ val, label, active, soon }) => (
                <button
                  key={val}
                  type="button"
                  disabled={!active}
                  onClick={() => active && toggleChannel(val)}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                    form.channels.includes(val)
                      ? 'border-primary/40 bg-primary/8 text-primary'
                      : 'border-border/60 text-muted-foreground'
                  } ${!active ? 'opacity-40 cursor-not-allowed' : 'hover:border-border'}`}
                >
                  {label}{soon ? ' · soon' : ''}
                </button>
              ))}
            </div>
          </div>

          {/* Custom message */}
          <div className="space-y-1.5">
            <Label htmlFor="rem-msg" className="text-sm font-medium text-muted-foreground">
              Custom message <span className="font-normal">(optional)</span>
            </Label>
            <Textarea
              id="rem-msg"
              value={form.custom_message}
              onChange={(e) => set('custom_message', e.target.value)}
              rows={2}
              placeholder="Leave blank to use the default tribute message."
              className="resize-none border-[#e8e2d8]"
            />
          </div>

          {/* Enabled toggle */}
          <div className="flex items-center gap-2">
            <input
              id="rem-enabled"
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => set('enabled', e.target.checked)}
              className="h-4 w-4 rounded border-[#e8e2d8]"
            />
            <Label htmlFor="rem-enabled" className="text-sm cursor-pointer">Enable this reminder</Label>
          </div>

          <div className="flex items-center gap-2 pt-1">
            <Button type="submit" className="rounded-full" disabled={saving}>
              {saving ? 'Saving…' : editReminder ? 'Save changes' : 'Add reminder'}
            </Button>
            <Button type="button" variant="ghost" className="rounded-full" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default AddReminderDialog;
